import { useState, FormEvent } from "react";
import { confirmSignUp, resendSignUpCode, autoSignIn } from "aws-amplify/auth";
import { TextInput } from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import { AuthEvent } from "../../../data/auth-events";
import styles from "./claimable-authenticator-v2.module.scss";

type EmailCodeEntryProps = {
  email: string;
  onVerified: () => void;
};

const EmailCodeEntry = ({ email, onVerified }: EmailCodeEntryProps) => {
  const posthog = usePostHog();
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError("Please enter the confirmation code");
      return;
    }

    posthog?.capture(AuthEvent.ConfirmCodeSubmit);
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode: trimmedCode,
      });

      if (result.nextStep.signUpStep === "DONE") {
        posthog?.capture(AuthEvent.ConfirmCodeSuccess);
        onVerified();
      } else {
        posthog?.capture(AuthEvent.ConfirmCodeSuccess);
        await autoSignIn();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Invalid verification code";
      posthog?.capture(AuthEvent.ConfirmCodeError, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    posthog?.capture(AuthEvent.ResendCode);
    setIsResending(true);
    setError(null);

    try {
      await resendSignUpCode({ username: email });
    } catch {
      setError("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={styles.emailCodeEntry}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <h1 className={styles.emailCodeTitle}>{"We Emailed You"}</h1>

      <p className={styles.emailCodeDescription}>
        {"Your code is on the way. To log in, enter the code we emailed to "}
        <strong>{email}</strong>
        {". It may take a minute to arrive."}
      </p>

      <p className={styles.emailCodeSpamNotice}>
        {"Don't see it? Check your spam, junk, or promotions folders."}
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Confirmation Code"
          placeholder="Enter your code"
          size="lg"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          autoFocus
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        {error && <p className={styles.formError}>{error}</p>}

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "CONFIRMING..." : "CONFIRM"}
        </button>

        <button
          type="button"
          className={styles.secondaryButton}
          onClick={handleResendCode}
          disabled={isResending}
        >
          {isResending ? "RESENDING..." : "RESEND CODE"}
        </button>
      </form>
    </div>
  );
};

export default EmailCodeEntry;
